import { memo, useEffect, useMemo, useState } from 'react';
import SvgIcon from '@picsart/rc/old/SvgIcon';
import { useTranslator } from '@picsart/rc/services/localization';

import { getTargetSizes } from 'pages/Batch/utils/process/resizeProcess';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import useMemoSelector from 'hooks/useMemoSelector';
import { getBatchActiveColor } from 'store/batch/selectors';
import getImageSize from 'pages/Batch/utils/resize/getImageSize';

import useStyles from './styles';

const ResizeGridItem = ({ imgSrc }: { imgSrc: string }) => {
  const activeColor = useMemoSelector(getBatchActiveColor);

  const translate = useTranslator();
  const { resizeParams } = useBatchData();
  const [imgSizes, setImgSizes] = useState([0, 0]);

  const [resizedWidth, resizedHeight] = useMemo(() => {
    const width = resizeParams.isOriginal ? imgSizes[0] : resizeParams.width;
    const height = resizeParams.isOriginal ? imgSizes[1] : resizeParams.height;
    return getTargetSizes(imgSizes[0], imgSizes[1], width, height);
  },
  [resizeParams, imgSizes]);

  const isMaxWidth = useMemo(() => resizedWidth > resizedHeight, [resizedWidth, resizedHeight]);
  const scale = useMemo(() => (isMaxWidth ? resizedHeight / resizedWidth : resizedWidth / resizedHeight) || 0, [isMaxWidth, resizedWidth, resizedHeight]);
  const classes = useStyles({
    resizeParams: {
      ...resizeParams,
      width: resizeParams.width || imgSizes[0],
      height: resizeParams.height || imgSizes[1],
    },
    resizedWidth,
    resizedHeight,
    scale,
    isMaxWidth,
    bgColor: activeColor,
  });

  const { width: sizeX, height: sizeY } = getImageSize(imgSizes[0], imgSizes[1]);

  useEffect(() => {
    const img = new Image();
    img.src = imgSrc;
    img.onload = () => {
      setImgSizes([img.width, img.height]);
    };
  }, [imgSrc]);

  return (
    <div className={classes.resizeItemContainer}>
      <div className={classes.resizeItem}>
        <img src={imgSrc} alt="img" className={classes.resizeImg} />
      </div>
      <div className={classes.titleContainer}>
        <p className={classes.sizeTitle}>
          {translate('{{sizeX}} x {{sizeY}}', {
            sizeX,
            sizeY,
          })}
        </p>

        {(imgSizes[0] !== resizedWidth || imgSizes[1] !== resizedHeight) && (
          <>
            <SvgIcon name="arrow-direct-right-gen" className={classes.arrowRight} />
            <p className={classes.sizeTitle}>
              {translate('{{resizedWidth}} x {{resizedHeight}}', {
                resizedWidth,
                resizedHeight,
              })}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(ResizeGridItem);
